import React, { useState, useEffect } from 'react';
import { Brain, Sparkles, Languages, Bot, Target, Activity, Zap } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const WhatsAppAIFeatures = ({ selectedConversation, messages, onSendMessage }) => {
  const [aiEnabled, setAiEnabled] = useState(true);
  const [smartReplies, setSmartReplies] = useState([]);
  const [sentimentAnalysis, setSentimentAnalysis] = useState(null);
  const [autoTranslation, setAutoTranslation] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [messageIntent, setMessageIntent] = useState(null);
  const [urgencyLevel, setUrgencyLevel] = useState('normal');
  const [suggestedAgent, setSuggestedAgent] = useState(null);
  const [aiInsights, setAiInsights] = useState([]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'te', name: 'Telugu', flag: '🇮🇳' },
    { code: 'ta', name: 'Tamil', flag: '🇮🇳' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' }
  ];

  // AI Functions
  const analyzeSentiment = async (text) => {
    try {
      const response = await api.post('/ai/sentiment-analysis', { text });
      if (response.data.success) {
        setSentimentAnalysis(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error('Error analyzing sentiment:', error);
    }
    return null;
  };

  const generateSmartReplies = async (context) => {
    try {
      const response = await api.post('/ai/smart-replies', { 
        context,
        conversationHistory: messages.slice(-10)
      });
      if (response.data.success) {
        setSmartReplies(response.data.data.suggestions);
        return response.data.data.suggestions;
      }
    } catch (error) {
      console.error('Error generating smart replies:', error);
    }
    return [];
  };

  const detectIntent = async (text) => {
    try {
      const response = await api.post('/ai/intent-detection', { text });
      if (response.data.success) {
        setMessageIntent(response.data.data.intent);
        return response.data.data.intent;
      }
    } catch (error) {
      console.error('Error detecting intent:', error);
    }
    return null;
  };

  const translateMessage = async (text, targetLang) => {
    try {
      const response = await api.post('/ai/translate', { 
        text, 
        targetLanguage: targetLang 
      });
      if (response.data.success) {
        return response.data.data.translatedText;
      }
    } catch (error) {
      console.error('Error translating message:', error);
    }
    return text;
  };

  const suggestAgent = async (intent, sentiment) => {
    try {
      const response = await api.post('/ai/agent-suggestion', { 
        intent, 
        sentiment,
        availableAgents: [
          { id: 1, name: "John Doe", specialty: "General Support" },
          { id: 2, name: "Jane Smith", specialty: "Technical Support" },
          { id: 3, name: "Mike Johnson", specialty: "Sales" }
        ]
      });
      if (response.data.success) {
        setSuggestedAgent(response.data.data.suggestedAgent);
        return response.data.data.suggestedAgent;
      }
    } catch (error) {
      console.error('Error suggesting agent:', error);
    }
    return null;
  };

  // Process incoming message with AI
  const processMessageWithAI = async (message) => {
    if (!aiEnabled) return;

    const text = message.message_content;
    
    // Analyze sentiment
    const sentiment = await analyzeSentiment(text);
    
    // Detect intent
    const intent = await detectIntent(text);
    
    // Generate smart replies
    const suggestions = await generateSmartReplies({
      message: text,
      intent,
      sentiment
    });

    // Suggest agent if needed
    if (intent && sentiment) {
      await suggestAgent(intent, sentiment.sentiment);
    }

    // Update urgency level based on sentiment
    if (sentiment) {
      const urgency = sentiment.score < 0.3 ? 'high' : 
                     sentiment.score < 0.6 ? 'medium' : 'normal';
      setUrgencyLevel(urgency);
    }

    // Generate insights
    const insights = [];
    if (sentiment) insights.push(`Sentiment: ${sentiment.sentiment} (${Math.round(sentiment.score * 100)}%)`);
    if (intent) insights.push(`Intent: ${intent}`);
    if (urgencyLevel !== 'normal') insights.push(`Urgency: ${urgencyLevel}`);
    
    setAiInsights(insights);
  };

  // Handle smart reply selection
  const handleSmartReply = (reply) => {
    onSendMessage(reply.text);
    setSmartReplies([]);
    toast.success('Smart reply sent!');
  };

  // Auto-translate message
  const handleAutoTranslate = async (text) => {
    if (autoTranslation && targetLanguage !== 'en') {
      const translated = await translateMessage(text, targetLanguage);
      return translated;
    }
    return text;
  };

  return (
    <div className="space-y-4">
      {/* AI Controls */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 flex items-center">
            <Brain className="w-5 h-5 mr-2" />
            AI Features
          </h3>
          <button
            onClick={() => setAiEnabled(!aiEnabled)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              aiEnabled 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
            }`}
          >
            {aiEnabled ? 'AI Active' : 'AI Inactive'}
          </button>
        </div>

        {/* AI Settings */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={autoTranslation}
                onChange={(e) => setAutoTranslation(e.target.checked)}
                className="rounded"
              />
              <Languages className="w-4 h-4" />
              <span>Auto Translation</span>
            </label>
            
            {autoTranslation && (
              <select
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                className="w-full px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700"
              >
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium">AI Model:</span>
              <span className="ml-2 text-gray-600 dark:text-gray-400">GPT-4o (Latest)</span>
            </div>
            <div className="text-sm">
              <span className="font-medium">Response Time:</span>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Fast</span>
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      {aiInsights.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center">
            <Activity className="w-4 h-4 mr-1" />
            AI Insights
          </h4>
          <div className="space-y-1">
            {aiInsights.map((insight, index) => (
              <div key={index} className="text-xs text-blue-700 dark:text-blue-300">
                {insight}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Smart Replies */}
      {smartReplies.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-2 flex items-center">
            <Sparkles className="w-4 h-4 mr-1" />
            Smart Replies
          </h4>
          <div className="space-y-2">
            {smartReplies.slice(0, 3).map((reply, index) => (
              <button
                key={index}
                onClick={() => handleSmartReply(reply)}
                className="w-full text-left p-2 text-sm bg-white dark:bg-gray-700 rounded border hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="font-medium">{reply.title}</div>
                <div className="text-gray-600 dark:text-gray-400 text-xs">{reply.text}</div>
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Confidence: {Math.round(reply.confidence * 100)}%
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sentiment Analysis */}
      {sentimentAnalysis && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-2 flex items-center">
            <Target className="w-4 h-4 mr-1" />
            Sentiment Analysis
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm">Sentiment:</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                sentimentAnalysis.sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                sentimentAnalysis.sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {sentimentAnalysis.sentiment}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Score:</span>
              <span className="text-sm font-medium">
                {Math.round(sentimentAnalysis.score * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  sentimentAnalysis.sentiment === 'positive' ? 'bg-green-500' :
                  sentimentAnalysis.sentiment === 'negative' ? 'bg-red-500' :
                  'bg-gray-500'
                }`}
                style={{ width: `${Math.abs(sentimentAnalysis.score) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Suggestion */}
      {suggestedAgent && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-indigo-900 dark:text-indigo-100 mb-2 flex items-center">
            <Bot className="w-4 h-4 mr-1" />
            AI Agent Suggestion
          </h4>
          <div className="text-sm">
            <div className="font-medium">{suggestedAgent.name}</div>
            <div className="text-indigo-600 dark:text-indigo-400">
              {suggestedAgent.specialty}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Recommended based on conversation analysis
            </div>
          </div>
        </div>
      )}

      {/* Quick AI Actions */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          <Zap className="w-4 h-4 mr-1" />
          Quick AI Actions
        </h4>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              const lastMessage = messages[messages.length - 1];
              if (lastMessage) {
                processMessageWithAI(lastMessage);
              }
            }}
            className="px-3 py-2 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Analyze Last Message
          </button>
          <button
            onClick={() => {
              const context = messages.slice(-5).map(m => m.message_content).join(' ');
              generateSmartReplies({ context });
            }}
            className="px-3 py-2 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            Generate Replies
          </button>
          <button
            onClick={() => {
              if (selectedConversation) {
                suggestAgent(messageIntent, sentimentAnalysis?.sentiment);
              }
            }}
            className="px-3 py-2 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
          >
            Suggest Agent
          </button>
          <button
            onClick={() => {
              setAiInsights([]);
              setSmartReplies([]);
              setSentimentAnalysis(null);
              setSuggestedAgent(null);
            }}
            className="px-3 py-2 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Clear AI Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppAIFeatures;
